import classNames from 'classnames';
import { memo, useEffect } from 'react';

import { FileExtensionPreview } from '@air/component-file-extension-preview';
import { exceedsBrowserImagePreviewLimit } from '@air/feature-uploads/utils';
import { Spinner } from '@air/primitive-spinner';
import { type Upload } from '@air/redux-uploader';

import { AssetGalleryCardMetaDetails } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardMetaDetails';
import { useUploadingData } from '~/components/Upload/hooks/useUploadingData';
import { UPLOADING_ASSET } from '~/constants/testIDs';
import { useHover } from '~/hooks/useHover';
import { isTitleAndMetadataVisibleSelector } from '~/store/configViews/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';
import { registerUploadPreview } from '~/utils/uploadPreviewCache';

export type UploadingAssetGalleryCardProps = {
  upload: Upload;
  width: number;
  getClassName?: (props: { isHovering: boolean }) => string;
};

const CARD_INNER_PADDING = 16;
const CARD_INNER_PADDING_METADATA = 4;

export const UploadingAssetGalleryCard = memo(({ upload, width, getClassName }: UploadingAssetGalleryCardProps) => {
  const {
    apiUploadData: { ext, size, importedName },
    imageBase64,
  } = upload;
  const { stateText } = useUploadingData(upload);
  const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
  const { handleHover, handleUnhover, isHovering } = useHover();
  const title = importedName || 'Untitled';

  useEffect(() => {
    if (!importedName) {
      return;
    }
    if (upload.file.type.startsWith('image/') && !exceedsBrowserImagePreviewLimit(upload.file.size)) {
      const objectUrl = URL.createObjectURL(upload.file);
      registerUploadPreview(importedName, objectUrl);
      // Don't revoke — keep alive for the processing phase after upload completes
    } else if (upload.file.type.startsWith('video/') && imageBase64) {
      registerUploadPreview(importedName, imageBase64);
    }
  }, [importedName, upload.file, imageBase64]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: mouse events are for hover state tracking only, not interactive actions
    <div
      data-testid={UPLOADING_ASSET}
      data-asset-title={title}
      onMouseEnter={handleHover}
      onMouseMove={handleHover}
      onMouseLeave={handleUnhover}
      className={classNames('relative flex h-full flex-col rounded-16', isHovering && 'ads:bg-hover-subtle bg-gray-3')}
    >
      <div
        className={classNames(
          'absolute -inset-1.5 cursor-default rounded-16 border-2 border-transparent',
          getClassName?.({ isHovering }),
        )}
      />
      <div className="relative flex grow items-center justify-center">
        {imageBase64 ? (
          <div className="absolute inset-1 overflow-hidden rounded-12">
            {/* biome-ignore lint/performance/noImgElement: blob/base64 preview URL, not eligible for Next.js Image optimization */}
            <img src={imageBase64} className="block min-h-full w-full object-cover" alt="" />
            <div className="shimmer-image-overlay absolute inset-0" />
            <div className="absolute top-3 left-3 flex items-center gap-1 rounded-4 bg-black/60 p-1 backdrop-blur-sm">
              <Spinner className="size-4 text-white" />
              <span className="font-medium text-10 text-white">{stateText}</span>
            </div>
            {!isTitleAndMetadataVisible && !!size && (
              <div
                className={classNames(
                  'absolute right-0 bottom-0 left-0 flex h-[100px] flex-col justify-end bg-gradient-to-b from-black/0 to-black/90 px-2 pt-1 pb-2',
                  isHovering ? 'opacity-100' : 'opacity-0',
                )}
              >
                <AssetGalleryCardMetaDetails
                  hasDarkBackground
                  clip={{
                    ext,
                    size,
                  }}
                  title={title}
                  width={width - CARD_INNER_PADDING}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="absolute inset-1 flex flex-col rounded-12 ads:bg-subtle bg-gray-4" />
            <div className="relative">
              <FileExtensionPreview
                ext={ext}
                text={stateText}
                color="gray"
                size="medium"
                className="grow justify-center"
              />
            </div>
          </>
        )}
      </div>
      {isTitleAndMetadataVisible && !!size && (
        <div className="relative">
          <AssetGalleryCardMetaDetails
            clip={{
              ext,
              size,
            }}
            title={title}
            width={width - CARD_INNER_PADDING_METADATA}
          />
        </div>
      )}
    </div>
  );
});

UploadingAssetGalleryCard.displayName = 'UploadingAssetGalleryCard';
